/**
 * Central SEO metadata for every public route rendered in TodoApp.jsx.
 * Consumed by <SeoRoute> (per-page <title>/<meta description>/canonical) and by
 * scripts/generate-sitemap.js (sitemap.xml) and scripts/react-snap-routes.js
 * (prerender route list) so all three stay in sync from one source of truth.
 *
 * Shape: { [path]: { title, description, canonicalPath?, noindex?, priority? } }
 */

const qa = (tech, extra = "") => ({
  title: `${tech} Interview Questions and Answers`,
  description: `Practice the most commonly asked ${tech} interview questions and answers with detailed explanations to help you prepare for your next technical interview.${extra}`,
});

const quiz = (tech) => ({
  title: `${tech} Quiz | Test Your Skills`,
  description: `Take a free ${tech} quiz to test your knowledge, get instant scoring, and identify gaps before your next interview or certification exam.`,
});

const systemDesignTopic = (topic, description) => ({
  title: `${topic} | System Design Guide`,
  description,
});

const privacyPolicy = (app) => ({
  title: `${app} Privacy Policy`,
  description: `Read the official privacy policy for ${app}, including what data is collected, how it is used, and how to contact us with questions.`,
});

const seoConfig = {
  // --- Home ---------------------------------------------------------------
  "/": {
    title: "Programming Interview Questions, Tutorials & System Design Guides",
    description:
      "Tech Venture LLC offers free interview questions and answers, tutorials, quizzes, and system design guides for Java, Python, React, AWS, Kafka, SQL, and more.",
  },
  "/home": {
    title: "Programming Interview Questions, Tutorials & System Design Guides",
    description:
      "Tech Venture LLC offers free interview questions and answers, tutorials, quizzes, and system design guides for Java, Python, React, AWS, Kafka, SQL, and more.",
    canonicalPath: "/",
  },

  // --- Technology Q&A hubs --------------------------------------------------
  "/questions": { ...qa("Java"), canonicalPath: "/java" },
  "/java": qa("Java"),
  "/kafka": qa("Apache Kafka"),
  "/python": qa("Python"),
  "/cassandra": qa("Cassandra"),
  "/react": qa("React"),
  "/sql": qa("SQL"),
  "/spring": qa("Spring & Spring Boot"),
  "/cSharp": qa("C#"),
  "/artificialIntelligence": qa("Artificial Intelligence"),
  "/hibernate": qa("Hibernate"),
  "/javascript": qa("JavaScript"),
  "/java8features": qa("Java 8 Features"),
  "/designPatterns": qa("Design Patterns"),
  "/blind75": {
    title: "Blind 75 Coding Interview Questions",
    description:
      "Solve the Blind 75 list of the most important coding interview questions, curated to cover every core data structures and algorithms pattern.",
  },
  "/serviceBus": qa("Azure Service Bus"),
  "/amazonWebServices": { ...qa("AWS (Amazon Web Services)"), canonicalPath: "/aws" },
  "/aws": qa("AWS (Amazon Web Services)"),
  "/dsa": qa("Data Structures and Algorithms"),
  "/kubernetes": qa("Kubernetes"),
  "/docker": qa("Docker"),
  "/terraform": qa("Terraform"),
  "/azure": qa("Microsoft Azure"),
  "/mongoDB": qa("MongoDB"),
  "/elasticSearch": qa("Elasticsearch"),
  "/angular": qa("Angular"),
  "/microservices": qa("Microservices"),
  "/behavioral": {
    title: "Behavioral Interview Questions and Answers",
    description:
      "Prepare for behavioral interviews with common STAR-method questions and sample answers used by top tech companies.",
  },
  "/restAPI": qa("REST API"),
  "/systemDesignInterviewQuestions": {
    title: "System Design Interview Questions and Answers",
    description:
      "Prepare for system design interviews with commonly asked questions, answers, and frameworks used at top tech companies.",
  },
  "/codingRound": {
    title: "Coding Round Interview Questions",
    description:
      "Practice common coding round interview questions asked in technical screening rounds at software companies.",
  },
  "/cosmos": qa("Azure Cosmos DB"),
  "/javalin": qa("Javalin"),

  // --- Quizzes / proficiency tests ------------------------------------------
  "/proficiencyTest": {
    title: "Free Technical Proficiency Tests",
    description:
      "Take free technical proficiency tests across Java, Python, JavaScript, SQL, and more to benchmark your skills before an interview.",
  },
  "/quiz": {
    title: "Programming Quizzes",
    description: "Browse free programming quizzes to test your knowledge across multiple technologies.",
  },
  "/quizApp": {
    title: "Programming Quiz App",
    description: "Take interactive programming quizzes and get instant feedback on your score.",
  },
  "/javaQuiz": quiz("Java"),
  "/javascriptQuiz": quiz("JavaScript"),
  "/sqlQuiz": quiz("SQL"),
  "/collectionApiQuiz": quiz("Java Collections API"),
  "/springQuiz": quiz("Spring Framework"),
  "/hibernateQuiz": quiz("Hibernate"),
  "/reactJSQuiz": quiz("React.js"),
  "/angularQuiz": quiz("Angular"),
  "/java8Quiz": quiz("Java 8"),
  "/pythonProficiencyTest": quiz("Python"),
  "/kafkaProficiencyTest": quiz("Kafka"),
  "/cassandraProficiencyTest": quiz("Cassandra"),

  // --- Blogs -----------------------------------------------------------------
  "/blogs": {
    title: "Tech Blog | Tutorials & Deep Dives",
    description:
      "Read in-depth tutorials and explainers on distributed systems, cloud infrastructure, and software engineering fundamentals.",
  },
  "/consul": {
    title: "HashiCorp Consul Explained",
    description: "Learn how HashiCorp Consul works for service discovery, health checking, and configuration in distributed systems.",
  },
  "/cdn": {
    title: "What Is a CDN? Content Delivery Network Explained",
    description: "Understand how a Content Delivery Network (CDN) works, why it improves performance, and when to use one.",
  },
  "/algorithms": {
    title: "Algorithms Explained with Examples",
    description: "Learn common algorithms, their time/space complexity, and when to use them, with clear examples.",
  },
  "/sdlc": {
    title: "Software Development Life Cycle (SDLC) Explained",
    description: "Understand the phases of the Software Development Life Cycle (SDLC) and common SDLC interview questions.",
  },
  "/git": {
    title: "Git Tutorial and Interview Questions",
    description: "Learn essential Git commands and workflows, plus commonly asked Git interview questions and answers.",
  },
  "/lambdaAndStepFunction": {
    title: "AWS Lambda and Step Functions Explained",
    description: "Learn how AWS Lambda and Step Functions work together to build serverless workflows.",
  },
  "/cache": {
    title: "Caching Explained: Strategies and Best Practices",
    description: "Learn common caching strategies, cache invalidation techniques, and best practices for scalable systems.",
  },
  "/blog/java/streamAPI": {
    title: "Java Stream API Tutorial",
    description: "Learn how to use the Java Stream API with practical examples for filtering, mapping, and collecting data.",
  },

  // --- System design ------------------------------------------------------
  "/systemDesign": {
    title: "System Design Interview Guide",
    description: "A complete system design interview guide covering scalability, databases, caching, messaging, and more.",
  },
  "/interview-game-plan": systemDesignTopic(
    "Interview Game Plan",
    "A step-by-step game plan for approaching system design interviews with confidence."
  ),
  "/performance-math-and-sizing": systemDesignTopic(
    "Performance Math and Capacity Sizing",
    "Learn how to estimate throughput, latency, and capacity for system design interviews using back-of-the-envelope math."
  ),
  "/internet-and-networking-fundamentals": systemDesignTopic(
    "Internet and Networking Fundamentals",
    "Core networking concepts every system design candidate should know: DNS, TCP/IP, load balancing, and more."
  ),
  "/storage-and-databases": systemDesignTopic(
    "Storage and Databases",
    "Compare SQL vs NoSQL, sharding, replication, and other database design decisions for system design interviews."
  ),
  "/caching": systemDesignTopic(
    "Caching",
    "Learn caching layers, eviction policies, and cache invalidation strategies used in large-scale system design."
  ),
  "/messaging-and-streaming": systemDesignTopic(
    "Messaging and Streaming",
    "Understand message queues, event streaming, and pub/sub patterns used in distributed system design."
  ),
  "/distributed-systems-theory": systemDesignTopic(
    "Distributed Systems Theory",
    "Learn CAP theorem, consistency models, and consensus algorithms for distributed systems design interviews."
  ),
  "/reliability-and-resilience": systemDesignTopic(
    "Reliability and Resilience",
    "Learn fault tolerance, redundancy, and failover patterns for building reliable, resilient systems."
  ),
  "/observability-and-operations": systemDesignTopic(
    "Observability and Operations",
    "Learn monitoring, logging, and alerting practices essential for operating systems at scale."
  ),
  "/apis-and-service-design": systemDesignTopic(
    "APIs and Service Design",
    "Best practices for designing scalable, maintainable APIs and services in a distributed architecture."
  ),
  "/security-privacy-compliance": systemDesignTopic(
    "Security, Privacy, and Compliance",
    "Understand security, privacy, and compliance considerations for system design interviews."
  ),
  "/compute-and-runtime": systemDesignTopic(
    "Compute and Runtime",
    "Compare compute options — VMs, containers, and serverless — for scalable system design."
  ),
  "/advanced-patterns": systemDesignTopic(
    "Advanced System Design Patterns",
    "Explore advanced system design patterns used to solve large-scale engineering problems."
  ),
  "/testing-and-quality-at-scale": systemDesignTopic(
    "Testing and Quality at Scale",
    "Learn testing strategies and quality practices for systems operating at large scale."
  ),
  "/cost-and-efficiency": systemDesignTopic(
    "Cost and Efficiency",
    "Learn how to design systems that balance performance with infrastructure cost efficiency."
  ),

  // --- Company / info pages -------------------------------------------------
  "/projects": {
    title: "Projects",
    description: "Explore sample software projects and portfolio work from Tech Venture LLC.",
  },
  "/interviewProcess": {
    title: "Interview Process Guide",
    description: "Understand what to expect at each stage of a typical software engineering interview process.",
  },
  "/all": {
    title: "All Interview Questions and Answers",
    description: "Browse the complete, paginated list of interview questions and answers across all technologies.",
    noindex: true,
  },
  "/privacyPolicyMain": {
    title: "Privacy Policy",
    description: "Read the Tech Venture LLC privacy policy covering how we collect, use, and protect your data.",
  },
  "/aboutTechVentureLLC": {
    title: "About Tech Venture LLC",
    description: "Learn about Tech Venture LLC's mission to help developers learn faster and prepare for technical interviews.",
  },
  "/aboutUs": {
    title: "About Us",
    description: "Learn more about Tech Venture LLC, based in the Washington DC metro area, serving developers globally.",
  },
  "/contactUs": {
    title: "Contact Us",
    description: "Get in touch with Tech Venture LLC for questions, feedback, or partnership opportunities.",
  },
  "/badges": {
    title: "Certificates and Badges",
    description: "View verified certificates and badges earned by completing Tech Venture LLC proficiency tests.",
  },
  "/search": {
    title: "Search",
    description: "Search Tech Venture LLC's interview questions, tutorials, and system design guides.",
  },
  "/learn": {
    title: "Learn",
    description: "Browse learning resources and tutorials to level up your software engineering skills.",
  },
  "/career": {
    title: "Careers at Tech Venture LLC",
    description: "Explore career opportunities at Tech Venture LLC and see how to apply.",
  },
  "/mobileApps": {
    title: "Mobile Apps by Tech Venture LLC",
    description: "Discover mobile apps built by Tech Venture LLC, available for Android and iOS.",
  },
  "/amazonProducts": {
    title: "Recommended Amazon Products",
    description: "Browse recommended books, gear, and tools for software engineers, curated by Tech Venture LLC.",
  },
  "/walmartProducts": {
    title: "Recommended Walmart Products",
    description: "Browse recommended tools and gear for software engineers, curated by Tech Venture LLC.",
  },
  "/prepareForInterview": {
    title: "How to Prepare for a Technical Interview",
    description: "A practical guide to preparing for your next technical interview, from resume to final round.",
  },

  // --- Profiles ---------------------------------------------------------------
  "/profile/rabin-pantha": {
    title: "Rabin Pantha | Profile",
    description: "View Rabin Pantha's professional profile, experience, and background.",
  },
  "/profile/khusbu-thapa": {
    title: "Khusbu Thapa | Profile",
    description: "View Khusbu Thapa's professional profile, experience, and background.",
  },

  // --- Privacy policies for affiliated apps -----------------------------------
  "/addressBookPrivacyPolicy": privacyPolicy("Address Book App"),
  "/vocabularyAppPrivacyPolicy": privacyPolicy("Vocabulary App"),
  "/interviewDojoApp": privacyPolicy("Interview Dojo App"),
  "/interviewDojoPrivacyPolicy": privacyPolicy("Interview Dojo"),

  // --- Auth-gated / utility pages: keep out of the index -----------------------
  "/login": { title: "Login", description: "Log in to your Tech Venture LLC account.", noindex: true },
  "/logout": { title: "Logout", description: "You have been logged out of Tech Venture LLC.", noindex: true },
  "/todos": { title: "My Todos", description: "Manage your todo list.", noindex: true },
};

export const NOT_FOUND_SEO = {
  title: "404 - Page Not Found",
  description: "The page you're looking for doesn't exist or may have moved.",
  noindex: true,
};

export default seoConfig;
